/**
 * Deliverables-only regeneration (2026-09-07) — the dashboard's service calls and
 * the payload the "Regenerate deliverables…" modal submits.
 *
 * A regeneration is `POST /rsa/process?engine=v2` with a `regenerate` block:
 *
 *   { "regenerate": { "source_run_id": "...", "targets": [...], "destination": "overwrite" | "dated_copies",
 *                     "collections": ["<collection name>", ...] },
 *     "project_id": "...", "user_email": "...", "output_folder_id": "<deliverables Drive folder>" }
 *
 * `collections` (scope selection, 2026-09-07) is the explicit list of the source run's
 * collections to regenerate — the modal resolves it from its Scope section so the backend
 * never has to re-derive it (the backend also accepts `analysis_types` / `directions`
 * filters; the GUI always sends the resolved names). Omitted = every collection.
 *
 * The Pipeline backend replays the SOURCE run's manifest (no analysis, no billing) and
 * publishes under the source run's prefix, so customer URLs stay valid. Only runs with
 * a manifest offer the action — `GET /rsa/v2/runs/manifests?run_ids=` says which.
 *
 * Pure helpers (no React) so they are unit-testable under CRA's Jest.
 */
import axios from "axios";
import { processApi, ENVIRONMENT } from "../../../API/api";

export const REGEN_TARGETS = [
  {
    id: "online_report",
    label: "Online report",
    hint: "Re-render every collection's HTML report in place (same URLs) with the current report image.",
  },
  {
    id: "video",
    label: "Videos",
    hint: "Re-compile the MP4s from the published frames.",
  },
  {
    id: "shapefile",
    label: "Shapefile + CSV + DXF",
    hint: "Re-export from the stored analysis data with today's export rules.",
  },
  {
    id: "automatic_reports",
    label: "Automatic reports",
    hint: "Re-generate the docx reports from the current Drive templates.",
  },
];
export const REGEN_TARGET_IDS = REGEN_TARGETS.map((t) => t.id);

export const REGEN_DESTINATIONS = [
  {
    id: "overwrite",
    label: "Overwrite in place",
    hint: "Same Drive folder, same file names, same published URLs (default).",
  },
  {
    id: "dated_copies",
    label: "Keep dated copies",
    hint: "The regenerated files carry today's date next to the originals (e.g. SSD_Report 2026-09-07.docx).",
  },
];
export const REGEN_DESTINATION_IDS = REGEN_DESTINATIONS.map((d) => d.id);
export const REGEN_DEFAULT_DESTINATION = "overwrite";

const RUN_ID_PROJECT_RE = /^project_(\d+)_run_/;

/** `project_2468_run_pyexisting_…` -> "2468"; "" for any other shape. */
export function projectIdFromRunId(runId) {
  const m = RUN_ID_PROJECT_RE.exec(String(runId || "").trim());
  return m ? m[1] : "";
}

/**
 * The same URL rule RSAAnalysis.js uses for a run: CloudFront's /rsa/* behaviour on the
 * deployed domains, the Pipeline origin otherwise. Injectable for tests.
 */
export function buildProcessUrl({
  environment = ENVIRONMENT,
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
  base = processApi,
} = {}) {
  const isProductionDomain = String(hostname || "").includes("123bimlive.com");
  const baseProcessUrl =
    environment === "production" || isProductionDomain ? "/rsa/process" : `${base}/process`;
  return `${baseProcessUrl}?engine=v2`;
}

export function manifestUrl(runId, base = processApi) {
  return `${base}/rsa/v2/runs/${encodeURIComponent(String(runId || ""))}/manifest`;
}

export function manifestsUrl(runIds, base = processApi) {
  const ids = (runIds || []).map((r) => String(r || "").trim()).filter(Boolean);
  return `${base}/rsa/v2/runs/manifests?run_ids=${encodeURIComponent(ids.join(","))}`;
}

/**
 * Build the /rsa/process body for a regeneration. Throws on an empty target list, a
 * missing source run id, or an explicitly EMPTY collection selection (an array with no
 * names) so the modal never posts an invalid request. `collections` undefined/null =
 * no scope = every collection of the source run.
 */
export function buildRegeneratePayload({
  sourceRunId,
  projectId,
  sourceProjectId,
  targets,
  destination,
  collections,
  userEmail,
  outputFolderId,
  projectName,
  projectType,
}) {
  const runId = String(sourceRunId || "").trim();
  if (!runId) throw new Error("A source run id is required to regenerate deliverables.");
  const wanted = new Set((targets || []).map((t) => String(t || "").trim().toLowerCase()));
  const chosen = REGEN_TARGET_IDS.filter((id) => wanted.has(id));
  if (!chosen.length) throw new Error("Select at least one deliverable to regenerate.");
  const dest = REGEN_DESTINATION_IDS.includes(destination) ? destination : REGEN_DEFAULT_DESTINATION;
  const pid = String(projectId ?? "").trim() || projectIdFromRunId(runId);
  let scope = null;
  if (collections !== undefined && collections !== null) {
    if (!Array.isArray(collections)) throw new Error("The collection scope must be a list of collection names.");
    scope = [...new Set(collections.map((c) => String(c || "").trim()).filter(Boolean))];
    if (!scope.length) throw new Error("Select at least one collection to regenerate.");
  }

  const payload = {
    regenerate: { source_run_id: runId, targets: chosen, destination: dest },
    project_id: pid,
    user_email: String(userEmail || ""),
  };
  if (scope) payload.regenerate.collections = scope;
  if (sourceProjectId !== undefined && sourceProjectId !== null && String(sourceProjectId).trim()) {
    payload.source_project_id = String(sourceProjectId).trim();
  }
  if (outputFolderId && String(outputFolderId).trim()) {
    payload.output_folder_id = String(outputFolderId).trim();
  }
  if (projectName) payload.project_name = projectName;
  if (projectType) payload.project_type = projectType;
  return payload;
}

// Pure helpers live in regenerateDeliverablesPure.js (no axios) so the dashboard's
// import graph stays Jest-safe; re-exported here for existing callers/tests.
export {
  isRunRegenerable,
  COLLECTION_DIRECTIONS,
  parseCollectionName,
  describeRunCollections,
  groupCollectionsByType,
  collectionDirections,
  toggleScopeGroup,
  resolveScopeSelection,
} from "./regenerateDeliverablesPure";

export async function fetchRunManifest(runId, { base = processApi } = {}) {
  const resp = await axios.get(manifestUrl(runId, base));
  return resp.data || { exists: false, run_id: runId };
}

/** `{ [run_id]: true|false }` for every id given (chunked; failures read as false). */
export async function fetchRegenerableRunIds(runIds, { base = processApi, chunkSize = 100 } = {}) {
  const ids = Array.from(new Set((runIds || []).map((r) => String(r || "").trim()).filter(Boolean)));
  const out = {};
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    try {
      const resp = await axios.get(manifestsUrl(chunk, base));
      const manifests = (resp.data && resp.data.manifests) || {};
      chunk.forEach((id) => {
        out[id] = Boolean(manifests[id]);
      });
    } catch (err) {
      chunk.forEach((id) => {
        out[id] = false;
      });
    }
  }
  return out;
}

export async function submitRegeneration(payload, { processUrl = buildProcessUrl() } = {}) {
  const resp = await axios.post(processUrl, payload, {
    headers: { "Content-Type": "application/json" },
  });
  return resp.data;
}
